/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @compiled/react */
import { useMemo } from 'react';
import { useZxcvbn } from './useZxcvbn';
import { getMinimumPasswordLength } from '../../utilities/password-policy';

const SPLITTER = /\W+/;

export const atlassianKeywords = [
  'atlassian',
  'bitbucket',
  'confluence',
  'jira',
  'opsgenie',
  'statuspage',
  'trello',
];

type ZxcvbnScoreRange = 0 | 1 | 2 | 3 | 4 | 5;

export type PasswordScore = ZxcvbnScoreRange | null;

type UsePasswordScoreProps = {
  displayName: string;
  email: string;
  password: string;
};

export const usePasswordScore = ({
  displayName,
  email,
  password,
}: UsePasswordScoreProps): { score: PasswordScore } => {
  const zxcvbn = useZxcvbn();
  const minimumPasswordLength = getMinimumPasswordLength();

  const scoreWords = useMemo(() => {
    return scoreWordSplitter([email, displayName, ...atlassianKeywords]);
  }, [displayName, email]);

  const zxcvbnResult = useMemo(() => {
    return zxcvbn && password ? zxcvbn(password, scoreWords) : null;
  }, [zxcvbn, password, scoreWords]);

  const score = useMemo(() => {
    if (
      password.length < minimumPasswordLength ||
      [email, displayName, ...atlassianKeywords].includes(password)
    ) {
      return 0;
    } else {
      const baseScore = zxcvbnResult?.score;
      return typeof baseScore === 'number' ? ((baseScore + 1) as ZxcvbnScoreRange) : null;
    }
  }, [password, displayName, email, minimumPasswordLength, zxcvbnResult]);

  return { score };
};

const addUnique = (seen: string[], chunk: string) =>
  seen.includes(chunk) ? seen : [...seen, chunk];

export function scoreWordSplitter(strings: string[]) {
  const deduped = strings.reduce(addUnique, []);
  return deduped.reduce((arr, str) => str.split(SPLITTER).reduce(addUnique, arr), deduped);
}
